import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Label } from "office-ui-fabric-react";
import { WindowWrapper } from "../../services/WindowWrapper";
import { TranslationService } from "../../services/TranslationService";
import { Logger } from "../../services/Logger";
import { OfficeWrapper } from "../../services/OfficeWrapper";
import { describeError } from "../../helpers/ErrorHelpers";
import { SharePointConsentRedirectService } from "../../services/SharePoint/SharePointConsentRedirectService";

export interface SharePointConsentInitiateComponentProps extends LocalizeContextProps {
    translationService: TranslationService;
    windowWrapper: WindowWrapper;
    redirectService: SharePointConsentRedirectService;
    logger: Logger;
    officeWrapper: OfficeWrapper;
}

// Same-origin bounce page for the SharePoint MSAL consent dialog (see SharePointConsentDialog.ts):
// the Office Dialog API requires the dialog's first URL to be same-origin/in the add-in's trusted
// domains, so this page loads first and then runs MSAL's own redirect flow (acquireTokenRedirect),
// which navigates the dialog to Microsoft's login page.
const SharePointConsentInitiateComponent: React.FC<SharePointConsentInitiateComponentProps> = (props) => {
    const [error, setError] = useState<string | null>(null);
    /** Raw error detail (errorCode/message), shown inline so it's visible without racing the dialog's DevTools. */
    const [debugInfo, setDebugInfo] = useState<string | null>(null);

    // This page is its own React root (no AppComponent), so translations must be initialised here, before first paint.
    const translationsInitialised = useRef(false);
    if (!translationsInitialised.current) {
        translationsInitialised.current = true;
        props.translationService.getTranslations(props);
    }

    useEffect(() => {
        // Tells the parent dialog (SharePointConsentDialog.ts) this attempt failed; otherwise its promise
        // would stay pending forever and the dialog would stay open with no way to retry.
        const failAndNotifyParent = (detail: string) => {
            setError("AUTHENTICATION.LOGIN_ERROR");
            setDebugInfo(detail);
            props.officeWrapper.messageParent(JSON.stringify({ error: detail }));
        };

        const scopesParam = props.windowWrapper.getQueryParam("scopes");
        const clientId = props.windowWrapper.getQueryParam("clientId");
        const authority = props.windowWrapper.getQueryParam("authority");
        const loginHint = props.windowWrapper.getQueryParam("loginHint") || undefined;
        const missing = [!scopesParam && "scopes", !clientId && "clientId", !authority && "authority"].filter(
            (name): name is string => !!name
        );
        if (missing.length > 0 || !scopesParam || !clientId || !authority) {
            const detail = `Missing ${missing.map((name) => `'${name}'`).join(", ")} query param`;
            props.logger.error(`SharePointConsentInitiateComponent: ${detail}`);
            failAndNotifyParent(detail);
            return;
        }

        props.redirectService
            .beginInteractiveRedirect({
                scopes: scopesParam.split(" "),
                clientId,
                authority,
                ...(loginHint ? { loginHint } : {}),
            })
            .catch((redirectError) => {
                props.logger.error("SharePointConsentInitiateComponent: acquireTokenRedirect failed", redirectError);
                failAndNotifyParent(describeError(redirectError));
            });
        // Runs once on mount: the dialog navigates away as soon as the redirect starts.
    }, []);

    return (
        <>
            {error ? <Label>{props.translate(error)}</Label> : null}
            {/* Debug detail — visible directly in the dialog since it errors before DevTools can be opened in time. */}
            {debugInfo ? <Label>{debugInfo}</Label> : null}
        </>
    );
};

export default withLocalize(SharePointConsentInitiateComponent);
